import React, { useEffect, useState, useRef, useMemo } from "react";
import axios from "axios";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Trash2, GripVertical, X, Check, Pencil, ChevronDown, ChevronUp, MessageSquareText, Copy } from "lucide-react";
import { toast } from "sonner";
import LoadingSpinner from "@/components/LoadingSpinner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/kanban`;

const Kanban = () => {
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [novaListaTitulo, setNovaListaTitulo] = useState("");
  const [adicionandoLista, setAdicionandoLista] = useState(false);
  const [editandoLista, setEditandoLista] = useState(null);
  const [adicionandoCard, setAdicionandoCard] = useState(null);
  const [novoCardTitulo, setNovoCardTitulo] = useState({});
  const [expandedCard, setExpandedCard] = useState(null);
  const [editandoCard, setEditandoCard] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: null, listaId: null, cardId: null, titulo: "" });
  const editListaRef = useRef(null);
  const boardRef = useRef(null);

  useEffect(() => { carregarBoard(); }, []);

  const listasOrdenadas = useMemo(() => {
    if (!board?.listas) return [];
    return [...board.listas]
      .sort((a, b) => a.posicao - b.posicao)
      .map((l) => ({ ...l, cards: [...(l.cards || [])].sort((a, b) => a.posicao - b.posicao) }));
  }, [board]);

  const carregarBoard = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/boards`);
      if (res.data.length > 0) {
        const b = res.data[0];
        // Mantém board.listas na mesma ordem exibida (por posicao) para o drag-and-drop
        b.listas = [...(b.listas || [])]
          .sort((a, c) => a.posicao - c.posicao)
          .map((l) => ({ ...l, cards: [...(l.cards || [])].sort((a, c) => a.posicao - c.posicao) }));
        setBoard(b);
      }
    } catch {
      toast.error("Erro ao carregar quadro");
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination || !board) return;
    const { source, destination, type } = result;
    const listas = [...board.listas];

    if (type === "LIST") {
      const [moved] = listas.splice(source.index, 1);
      listas.splice(destination.index, 0, moved);
      listas.forEach((l, i) => (l.posicao = i));
    } else {
      const srcList = listas.find((l) => l.id === source.droppableId);
      const dstList = listas.find((l) => l.id === destination.droppableId);
      if (!srcList || !dstList) return;
      if (srcList.id === dstList.id) {
        const cards = [...srcList.cards];
        const [moved] = cards.splice(source.index, 1);
        cards.splice(destination.index, 0, moved);
        cards.forEach((c, i) => (c.posicao = i));
        srcList.cards = cards;
      } else {
        const srcCards = [...srcList.cards];
        const dstCards = [...dstList.cards];
        const [moved] = srcCards.splice(source.index, 1);
        dstCards.splice(destination.index, 0, moved);
        srcCards.forEach((c, i) => (c.posicao = i));
        dstCards.forEach((c, i) => (c.posicao = i));
        srcList.cards = srcCards;
        dstList.cards = dstCards;
      }
    }
    setBoard({ ...board, listas });
    try {
      await axios.put(`${API}/boards/${board.id}/reorder`, listas);
    } catch {
      toast.error("Erro ao salvar reordenacao");
      carregarBoard();
    }
  };

  const adicionarLista = async () => {
    if (!novaListaTitulo.trim() || !board) return;
    try {
      const res = await axios.post(`${API}/boards/${board.id}/listas?titulo=${encodeURIComponent(novaListaTitulo.trim())}`);
      setBoard({ ...board, listas: [...board.listas, res.data] });
      setNovaListaTitulo("");
      setAdicionandoLista(false);
      toast.success("Lista adicionada");
    } catch { toast.error("Erro ao adicionar lista"); }
  };

  const deletarLista = async (listaId) => {
    if (!board) return;
    try {
      await axios.delete(`${API}/boards/${board.id}/listas/${listaId}`);
      setBoard({ ...board, listas: board.listas.filter((l) => l.id !== listaId) });
      toast.success("Lista removida");
    } catch { toast.error("Erro ao remover lista"); }
  };
  
  const confirmarDeletarLista = (listaId, titulo) => {
    setDeleteDialog({ open: true, type: "lista", listaId, cardId: null, titulo });
  };

  const atualizarListaTitulo = async (listaId, titulo) => {
    if (!board || !titulo.trim()) return;
    try {
      await axios.put(`${API}/boards/${board.id}/listas/${listaId}?titulo=${encodeURIComponent(titulo.trim())}`);
      setBoard({ ...board, listas: board.listas.map((l) => (l.id === listaId ? { ...l, titulo: titulo.trim() } : l)) });
      setEditandoLista(null);
    } catch { toast.error("Erro ao atualizar lista"); }
  };

  const adicionarCard = async (listaId) => {
    const titulo = novoCardTitulo[listaId]?.trim();
    if (!titulo || !board) return;
    try {
      const res = await axios.post(`${API}/boards/${board.id}/listas/${listaId}/cards?titulo=${encodeURIComponent(titulo)}`);
      setBoard({
        ...board,
        listas: board.listas.map((l) => l.id === listaId ? { ...l, cards: [...l.cards, res.data] } : l),
      });
      setNovoCardTitulo({ ...novoCardTitulo, [listaId]: "" });
      setAdicionandoCard(null);
      toast.success("Card adicionado");
    } catch { toast.error("Erro ao adicionar card"); }
  };

  const atualizarCard = async (listaId, cardId, titulo, descricao) => {
    if (!board) return;
    try {
      const params = new URLSearchParams();
      if (titulo !== undefined) params.set("titulo", titulo);
      if (descricao !== undefined) params.set("descricao", descricao);
      await axios.put(`${API}/boards/${board.id}/listas/${listaId}/cards/${cardId}?${params.toString()}`);
      setBoard({
        ...board,
        listas: board.listas.map((l) =>
          l.id === listaId
            ? { ...l, cards: l.cards.map((c) => c.id === cardId ? { ...c, ...(titulo !== undefined && { titulo }), ...(descricao !== undefined && { descricao }) } : c) }
            : l
        ),
      });
      setEditandoCard(null);
      toast.success("Card atualizado");
    } catch { toast.error("Erro ao atualizar card"); }
  };

  const deletarCard = async (listaId, cardId) => {
    if (!board) return;
    try {
      await axios.delete(`${API}/boards/${board.id}/listas/${listaId}/cards/${cardId}`);
      setBoard({
        ...board,
        listas: board.listas.map((l) => l.id === listaId ? { ...l, cards: l.cards.filter((c) => c.id !== cardId) } : l),
      });
      if (expandedCard === cardId) setExpandedCard(null);
      toast.success("Card removido");
    } catch { toast.error("Erro ao remover card"); }
  };
  
  const confirmarDeletarCard = (listaId, cardId, titulo) => {
    setDeleteDialog({ open: true, type: "card", listaId, cardId, titulo });
  };
  
  const handleConfirmDelete = () => {
    if (deleteDialog.type === "lista") {
      deletarLista(deleteDialog.listaId);
    } else if (deleteDialog.type === "card") {
      deletarCard(deleteDialog.listaId, deleteDialog.cardId);
    }
    setDeleteDialog({ open: false, type: null, listaId: null, cardId: null, titulo: "" });
  };

  if (loading) return <LoadingSpinner message="Carregando mensagens..." />;
  if (!board) return <div className="text-center py-12 text-slate-500">Nenhum quadro encontrado</div>;

  const totalCards = board.listas.reduce((s, l) => s + l.cards.length, 0);

  return (
    <div className="h-full flex flex-col -m-6 md:-m-8 lg:-m-10" data-testid="mensagens-container">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-3 bg-white border-b border-slate-200 flex-shrink-0">
        <div className="flex items-center gap-3">
          <MessageSquareText className="w-6 h-6 text-slate-700" strokeWidth={1.5} />
          <div>
            <h1 className="text-xl font-bold text-slate-900" data-testid="mensagens-title">Mensagens</h1>
            <p className="text-xs text-slate-500">{board.listas.length} listas - {totalCards} cards</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => setAdicionandoLista(true)} data-testid="add-list-button">
          <Plus className="w-4 h-4 mr-1.5" /> Nova Lista
        </Button>
      </div>

      {/* Board */}
      <div
        ref={boardRef}
        className="flex-1 overflow-x-auto overflow-y-hidden"
        style={{ WebkitOverflowScrolling: "touch", scrollBehavior: "smooth" }}
      >
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="board" type="LIST" direction="horizontal">
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className="flex gap-3 p-4 h-full items-start"
                style={{ minWidth: "max-content" }}
                data-testid="mensagens-board"
              >
                {listasOrdenadas.map((lista, index) => (
                    <Draggable key={lista.id} draggableId={lista.id} index={index}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className={`w-[280px] flex-shrink-0 flex flex-col rounded-xl bg-white border border-slate-200 shadow-sm transition-shadow ${
                            snapshot.isDragging ? "shadow-lg ring-2 ring-slate-300" : ""
                          }`}
                          style={{ ...provided.draggableProps.style, maxHeight: "calc(100vh - 180px)" }}
                          data-testid={`list-${lista.id}`}
                        >
                          {/* List Header */}
                          <div
                            {...provided.dragHandleProps}
                            className="flex items-center gap-2 px-3 py-2 border-b border-slate-100 flex-shrink-0 rounded-t-xl"
                            style={{ borderTop: `3px solid ${lista.cor || "#64748b"}` }}
                          >
                            <GripVertical className="w-3.5 h-3.5 text-slate-300 flex-shrink-0 cursor-grab" />
                            {editandoLista === lista.id ? (
                              <form onSubmit={(e) => { e.preventDefault(); atualizarListaTitulo(lista.id, editListaRef.current?.value || lista.titulo); }} className="flex-1 flex gap-1">
                                <Input ref={editListaRef} defaultValue={lista.titulo} className="h-7 text-sm font-semibold" autoFocus
                                  onBlur={() => atualizarListaTitulo(lista.id, editListaRef.current?.value || lista.titulo)}
                                  data-testid={`edit-list-title-${lista.id}`} />
                              </form>
                            ) : (
                              <span className="flex-1 text-sm font-semibold text-slate-800 cursor-pointer truncate"
                                onDoubleClick={() => setEditandoLista(lista.id)} title="Duplo clique para editar">
                                {lista.titulo}
                              </span>
                            )}
                            <span className="text-[10px] text-slate-400 font-medium bg-slate-50 rounded-full px-1.5 py-0.5 flex-shrink-0">{lista.cards.length}</span>
                            <button onClick={() => confirmarDeletarLista(lista.id, lista.titulo)} className="text-slate-300 hover:text-red-500 transition-colors p-0.5"
                              data-testid={`delete-list-${lista.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>

                          {/* Cards */}
                          <Droppable droppableId={lista.id} type="CARD">
                            {(provided, snapshot) => (
                              <div ref={provided.innerRef} {...provided.droppableProps}
                                className={`flex-1 overflow-y-auto px-2 py-1.5 space-y-1.5 min-h-[32px] ${snapshot.isDraggingOver ? "bg-slate-50" : ""}`}>
                                {lista.cards.map((card, cardIndex) => (
                                    <Draggable key={card.id} draggableId={card.id} index={cardIndex}>
                                      {(provided, snapshot) => (
                                        <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}
                                          className={`group rounded-lg border bg-white cursor-grab active:cursor-grabbing transition-all ${
                                            snapshot.isDragging ? "shadow-lg ring-2 ring-blue-200" : "border-slate-100 hover:border-slate-200 hover:shadow-sm"
                                          }`}
                                          data-testid={`card-${card.id}`}>

                                          {editandoCard === card.id ? (
                                            <EditCardForm card={card}
                                              onSave={(t, d) => atualizarCard(lista.id, card.id, t, d)}
                                              onCancel={() => setEditandoCard(null)} />
                                          ) : (
                                            <>
                                              {/* Card Title Row */}
                                              <div className="flex items-start gap-1 px-2.5 pt-2 pb-1.5">
                                                <button
                                                  onClick={(e) => { e.stopPropagation(); setExpandedCard(expandedCard === card.id ? null : card.id); }}
                                                  className="flex-1 text-left min-w-0"
                                                  data-testid={`card-title-${card.id}`}>
                                                  <span className="text-[13px] font-medium text-slate-800 leading-tight block">
                                                    {card.titulo || card.conteudo || "Sem titulo"}
                                                  </span>
                                                  {card.descricao && expandedCard !== card.id && (
                                                    <span className="text-[10px] text-slate-400 mt-0.5 flex items-center gap-0.5">
                                                      <MessageSquareText className="w-2.5 h-2.5" /> Descricao
                                                    </span>
                                                  )}
                                                </button>
                                                <div className="flex gap-0.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                                                  <button onClick={(e) => { e.stopPropagation(); setEditandoCard(card.id); }}
                                                    className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600"
                                                    data-testid={`edit-card-${card.id}`}>
                                                    <Pencil className="w-3 h-3" />
                                                  </button>
                                                  <button onClick={(e) => {
                                                    e.stopPropagation();
                                                    const texto = card.descricao || "";
                                                    navigator.clipboard.writeText(texto);
                                                    toast.success("Mensagem copiada!");
                                                  }}
                                                    className="p-1 rounded hover:bg-blue-50 text-slate-400 hover:text-blue-500"
                                                    data-testid={`copy-card-${card.id}`}
                                                    title="Copiar">
                                                    <Copy className="w-3 h-3" />
                                                  </button>
                                                  <button onClick={(e) => { e.stopPropagation(); confirmarDeletarCard(lista.id, card.id, card.titulo || card.conteudo || "Sem titulo"); }}
                                                    className="p-1 rounded hover:bg-red-50 text-slate-400 hover:text-red-500"
                                                    data-testid={`delete-card-${card.id}`}>
                                                    <Trash2 className="w-3 h-3" />
                                                  </button>
                                                </div>
                                              </div>

                                              {/* Expanded Description */}
                                              {expandedCard === card.id && (
                                                <div className="px-2.5 pb-2 border-t border-slate-50 mt-0.5 pt-1.5" data-testid={`card-desc-${card.id}`}>
                                                  {card.descricao ? (
                                                    <div className="space-y-1">
                                                      {card.descricao.split("\n").map((linha, li) => (
                                                        linha.trim() === "" ? null : (
                                                          <div key={`${card.id}-linha-${li}`} className="flex items-start gap-1">
                                                            <p className="text-xs text-slate-600 whitespace-pre-wrap break-words leading-relaxed flex-1">{linha}</p>
                                                            <button onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(linha); toast.success("Linha copiada!"); }}
                                                              className="p-0.5 rounded hover:bg-blue-50 text-slate-300 hover:text-blue-500 shrink-0"
                                                              title="Copiar esta linha" data-testid={`copy-line-${card.id}-${li}`}>
                                                              <Copy className="w-3 h-3" />
                                                            </button>
                                                          </div>
                                                        )
                                                      ))}
                                                      <button onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(card.descricao); toast.success("Mensagem copiada!"); }}
                                                        className="text-[10px] text-blue-500 hover:text-blue-700 font-medium mt-1 block"
                                                        data-testid={`copy-all-${card.id}`}>
                                                        Copiar mensagem inteira
                                                      </button>
                                                    </div>
                                                  ) : (
                                                    <button
                                                      onClick={(e) => { e.stopPropagation(); setEditandoCard(card.id); }}
                                                      className="text-xs text-slate-400 hover:text-slate-600 italic">
                                                      + Adicionar descricao...
                                                    </button>
                                                  )}
                                                  <button
                                                    onClick={(e) => { e.stopPropagation(); setEditandoCard(card.id); }}
                                                    className="text-[10px] text-blue-500 hover:text-blue-700 mt-1 block font-medium"
                                                    data-testid={`edit-desc-${card.id}`}>
                                                    Editar
                                                  </button>
                                                </div>
                                              )}
                                            </>
                                          )}
                                        </div>
                                      )}
                                    </Draggable>
                                  ))}
                                {provided.placeholder}
                              </div>
                            )}
                          </Droppable>

                          {/* Add Card */}
                          <div className="px-2 py-1.5 border-t border-slate-50 flex-shrink-0">
                            {adicionandoCard === lista.id ? (
                              <div className="space-y-1.5">
                                <Input
                                  value={novoCardTitulo[lista.id] || ""}
                                  onChange={(e) => setNovoCardTitulo({ ...novoCardTitulo, [lista.id]: e.target.value })}
                                  placeholder="Titulo do card..."
                                  className="h-8 text-sm"
                                  autoFocus
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") adicionarCard(lista.id);
                                    if (e.key === "Escape") setAdicionandoCard(null);
                                  }}
                                  data-testid={`new-card-input-${lista.id}`} />
                                <div className="flex gap-1.5">
                                  <Button size="sm" className="h-7 text-xs" onClick={() => adicionarCard(lista.id)} data-testid={`confirm-add-card-${lista.id}`}>
                                    <Check className="w-3 h-3 mr-1" /> Adicionar
                                  </Button>
                                  <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setAdicionandoCard(null)}>
                                    <X className="w-3 h-3" />
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <button onClick={() => setAdicionandoCard(lista.id)}
                                className="w-full flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-md px-2 py-1.5 transition-colors"
                                data-testid={`add-card-btn-${lista.id}`}>
                                <Plus className="w-3.5 h-3.5" /> Adicionar card
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                {provided.placeholder}

                {/* New List */}
                {adicionandoLista ? (
                  <div className="w-[280px] flex-shrink-0 rounded-xl bg-white border border-slate-200 p-3 shadow-sm">
                    <Input value={novaListaTitulo} onChange={(e) => setNovaListaTitulo(e.target.value)}
                      placeholder="Titulo da lista..." autoFocus className="mb-2"
                      onKeyDown={(e) => { if (e.key === "Enter") adicionarLista(); if (e.key === "Escape") setAdicionandoLista(false); }}
                      data-testid="new-list-title-input" />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={adicionarLista} data-testid="confirm-add-list">
                        <Check className="w-3.5 h-3.5 mr-1" /> Criar
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setAdicionandoLista(false)}>
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <button onClick={() => setAdicionandoLista(true)}
                    className="w-[280px] flex-shrink-0 rounded-xl border-2 border-dashed border-slate-200 hover:border-slate-300 bg-white/50 hover:bg-white p-4 flex items-center justify-center gap-2 text-sm text-slate-400 hover:text-slate-600 transition-all"
                    data-testid="add-list-placeholder">
                    <Plus className="w-4 h-4" /> Nova Lista
                  </button>
                )}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ ...deleteDialog, open: false })}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Voce tem certeza que quer excluir?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteDialog.type === "lista" 
                ? `A lista "${deleteDialog.titulo}" e todos os seus cards serao removidos permanentemente.`
                : `O card "${deleteDialog.titulo}" sera removido permanentemente.`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">Nao</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete} className="bg-red-600 hover:bg-red-700" data-testid="delete-confirm-btn">
              Sim, excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const EditCardForm = ({ card, onSave, onCancel }) => {
  const [titulo, setTitulo] = useState(card.titulo || card.conteudo || "");
  const [descricao, setDescricao] = useState(card.descricao || "");
  const descRef = useRef(null);

  useEffect(() => {
    if (descRef.current) {
      descRef.current.style.height = "auto";
      descRef.current.style.height = Math.min(descRef.current.scrollHeight, 200) + "px";
    }
  }, [descricao]);

  const handleSave = () => {
    if (!titulo.trim()) return;
    onSave(titulo.trim(), descricao.trim());
  };

  return (
    <div className="p-2.5 space-y-2" onClick={(e) => e.stopPropagation()} data-testid={`edit-form-${card.id}`}>
      <div>
        <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Titulo</label>
        <Input value={titulo} onChange={(e) => setTitulo(e.target.value)} className="h-8 text-sm mt-0.5" autoFocus
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSave(); } if (e.key === "Escape") onCancel(); }}
          data-testid={`edit-titulo-${card.id}`} />
      </div>
      <div>
        <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Descricao</label>
        <textarea ref={descRef} value={descricao}
          onChange={(e) => { setDescricao(e.target.value); }}
          placeholder="Adicionar descricao..."
          className="w-full text-xs border border-slate-200 rounded-md p-2 resize-none focus:outline-none focus:ring-2 focus:ring-slate-300 min-h-[48px] max-h-[200px] mt-0.5"
          onKeyDown={(e) => { if (e.key === "Escape") onCancel(); }}
          data-testid={`edit-descricao-${card.id}`} />
      </div>
      <div className="flex gap-1.5">
        <Button size="sm" className="h-6 text-xs px-2.5" onClick={handleSave} data-testid={`save-card-${card.id}`}>
          <Check className="w-3 h-3 mr-0.5" /> Salvar
        </Button>
        <Button size="sm" variant="ghost" className="h-6 text-xs px-2" onClick={onCancel}>
          <X className="w-3 h-3" /> Cancelar
        </Button>
      </div>
    </div>
  );
};

export default Kanban;
